/**
 * talk·point — Lazy-Mount für Shopifys Dynamic-Checkout (PayPal/Wallets).
 *
 * Der {{ form | payment_button }}-Output liegt in einem inerten <template>, damit
 * <shopify-accelerated-checkout> NICHT beim Laden upgradet.
 *
 * Was das spart: den www.paypal.com/sdk/js-Request, PayPals eigenes JS und den
 * iframe-Render. Was es NICHT spart: portable-wallets.de.js (~392 KB decoded) —
 * das hängt als blockierendes <script> ohne defer/async im <head> und wird von
 * Shopify über content_for_header injiziert, unabhängig von diesem Gate. Die
 * Custom-Component ist deshalb schon zur Ladezeit definiert; der Mount selbst
 * kostet gemessen ~32 ms bis zum echten Button und erzeugt keinen Long Task.
 *
 * Weil der Mount so billig ist, mounten wir bewusst FRÜH — beim ersten
 * Handkontakt, nicht erst beim Klick. pointermove feuert auf Desktop beim ersten
 * Mausmillimeter, touchstart am Anfang der ersten Wischgeste. Damit steht der
 * echte Button praktisch immer bereit, bevor jemand ihn braucht.
 *
 * 'scroll' bleibt bewusst draußen: Lighthouse löst am Ende des Laufs ein
 * synthetisches scroll aus, das würde die Messung verfälschen. 'pointerover'
 * ebenfalls: das feuert schon, wenn beim Laden Inhalt unter einen stehenden
 * Cursor rutscht — ohne jede Nutzeraktion, was das Gate auf Desktop faktisch
 * eager machen würde. Die gewählten Events emittiert ein Lighthouse-Nav-Run nie
 * und sie setzen echte Nutzerpräsenz voraus. Import-frei.
 */
(function () {
  var EVENTS = ['pointermove', 'pointerdown', 'keydown', 'wheel', 'touchstart'];
  var OPTS = { capture: true, passive: true };
  var SELECTOR = '[data-tp-paypal-mount]';
  // Notbremse: Der Platzhalter darf niemals dauerhaft „lädt“ signalisieren –
  // genau dieser Zustand war der Grund für den Umbau.
  var SETTLE_TIMEOUT = 8000;

  // Merkt sich, ob der auslösende Intent ein Druck INNERHALB des Mounts war.
  // Dann verschluckt der Mount den Klick (Ziel-Element verschwindet, bevor
  // 'click' feuert) und wir geben dem echten Button wenigstens den Fokus.
  var focusAfterMount = false;

  function teardown() {
    EVENTS.forEach(function (e) { window.removeEventListener(e, onIntent, OPTS); });
  }

  /** Ist der echte Wallet-Button sichtbar im DOM angekommen? */
  function isReady(mountEl) {
    return !!mountEl.querySelector('iframe, .shopify-payment-button__button');
  }

  /** Platzhalter entfernen, Ladezustand beenden, ggf. Fokus nachreichen. */
  function settle(mountEl) {
    if (mountEl.dataset.tpPaypalSettled) return;
    mountEl.dataset.tpPaypalSettled = '1';
    mountEl.classList.remove('is-loading');
    var skel = mountEl.querySelector('.tp-pdp__paypal-skel');
    if (skel) skel.remove();

    if (!focusAfterMount) return;
    focusAfterMount = false;
    // Bewusst KEIN synthetischer click(): PayPal öffnet ein Popup, ein
    // programmatischer Klick außerhalb der User-Geste läuft in den Popup-Blocker.
    focusWhenReady(mountEl, 60);
  }

  /**
   * Fokus auf den echten Button nachreichen. Der Mount settled im selben Tick,
   * da ist PayPals iframe noch nicht fokussierbar – deshalb über ein paar Frames
   * nachfassen (~1 s), bis es greift.
   */
  function focusWhenReady(mountEl, attempts) {
    var target = mountEl.querySelector('iframe, .shopify-payment-button__button');
    if (target && target.focus) {
      try { target.focus({ preventScroll: true }); } catch (err) { /* egal */ }
      if (mountEl.contains(document.activeElement)) return;
    }
    if (attempts <= 0) return;
    requestAnimationFrame(function () { focusWhenReady(mountEl, attempts - 1); });
  }

  /** Auf das Erscheinen des echten Buttons warten – mit hartem Timeout. */
  function watch(mountEl) {
    if (isReady(mountEl)) { settle(mountEl); return; }
    var obs = new MutationObserver(function () {
      if (!isReady(mountEl)) return;
      obs.disconnect();
      settle(mountEl);
    });
    obs.observe(mountEl, { childList: true, subtree: true });
    setTimeout(function () { obs.disconnect(); settle(mountEl); }, SETTLE_TIMEOUT);
  }

  /** Steht noch ein ungemounteter Button im DOM? */
  function pending() {
    return !!document.querySelector(SELECTOR + ' template[data-tp-paypal]');
  }

  function mount() {
    var mounts = document.querySelectorAll(SELECTOR);
    Array.prototype.forEach.call(mounts, function (m) {
      var tpl = m.querySelector('template[data-tp-paypal]');
      if (!tpl) return;
      // 0-%-Artikel ohne Betreibererklärung (IT#16795): bewusst NICHT mounten —
      // solange gesperrt ist, lädt die Seite auch kein PayPal-SDK.
      // assets/tp-pv-gate.js nimmt das Attribut nach dem Consent weg und ruft
      // window.tpPaypalMount() auf.
      if (m.hasAttribute('data-tp-pv-lock')) return;
      // Ab jetzt lädt tatsächlich etwas → Shimmer ist ehrlich.
      m.classList.add('is-loading');
      // Template-Inhalt ins Live-DOM heben → Component upgradet, SDK lädt.
      m.insertBefore(tpl.content.cloneNode(true), tpl);
      tpl.remove();
      watch(m);
    });
    // Listener nur abbauen, wenn nichts mehr aussteht — sonst käme ein gesperrter
    // Mount nach der Freigabe nicht mehr dran.
    if (!pending()) teardown();
  }

  // Nachträglicher Mount aus dem 0-%-Gate heraus.
  window.tpPaypalMount = mount;

  function onIntent(e) {
    if (e.type === 'pointerdown' && e.target && e.target.closest && e.target.closest(SELECTOR)) {
      focusAfterMount = true;
    }
    mount();
  }

  function arm() {
    if (!document.querySelector(SELECTOR + ' template[data-tp-paypal]')) return;
    EVENTS.forEach(function (e) { window.addEventListener(e, onIntent, OPTS); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', arm);
  else arm();
})();
